import { ClientPortalRole, DataGatewayRole } from 'src/types';
import { isAzureTargetPlatform } from 'src/utils/constants';

type RouteRole = ClientPortalRole | DataGatewayRole;

// Reusable role groups — the single source of truth for every route's
// allowList. Both `RootRouter` (route guards) and `Sidebar` (menu config)
// must read from `routeAccess` so the guard and the menu can never drift
// (a menu item showing a route the guard denies, or vice-versa).
const SUPER_ONLY: RouteRole[] = [ClientPortalRole.SuperAdmin];
const SUPER_SUPPORT: RouteRole[] = [ClientPortalRole.SuperAdmin, ClientPortalRole.Support];
const ANALYST_UP: RouteRole[] = [ClientPortalRole.SuperAdmin, ClientPortalRole.Support, ClientPortalRole.Analyst];
const CLIENT_ADMIN_ONLY: RouteRole[] = [ClientPortalRole.ClientAdmin];
const STANDARD_ADMINS: RouteRole[] = [
  ClientPortalRole.SuperAdmin,
  ClientPortalRole.Support,
  ClientPortalRole.Analyst,
  ClientPortalRole.ClientAdmin,
];
const EXTENDED_ADMINS: RouteRole[] = [
  ClientPortalRole.SuperAdmin,
  ClientPortalRole.Support,
  ClientPortalRole.Analyst,
  ClientPortalRole.ClientAdmin,
  ClientPortalRole.EmployerAdmin,
  ClientPortalRole.Viewer,
];
const BULK_UPLOAD_CONFIG_ADMINS: RouteRole[] = [
  ClientPortalRole.SuperAdmin,
  ClientPortalRole.Support,
  ClientPortalRole.Analyst,
  ClientPortalRole.ClientAdmin,
  ClientPortalRole.EmployerAdmin,
];
const ALL_DASHBOARD_ROLES: RouteRole[] = [
  ClientPortalRole.SuperAdmin,
  ClientPortalRole.Support,
  ClientPortalRole.Analyst,
  ClientPortalRole.ClientAdmin,
  ClientPortalRole.Viewer,
  ClientPortalRole.Manager,
  ClientPortalRole.EmployerAdmin,
  ClientPortalRole.CustomerOperations,
];
const DATA_GATEWAY_STANDARD: RouteRole[] = [
  DataGatewayRole.SuperAdmin,
  DataGatewayRole.Support,
  DataGatewayRole.Analyst,
  DataGatewayRole.ClientAdmin,
];
const DATA_GATEWAY_ANALYST_UP: RouteRole[] = [
  DataGatewayRole.SuperAdmin,
  DataGatewayRole.Support,
  DataGatewayRole.Analyst,
];
const DATA_GATEWAY_SUPER_SUPPORT: RouteRole[] = [DataGatewayRole.SuperAdmin, DataGatewayRole.Support];

// Azure-target builds disable the Data Access and API Integrations areas.
const dataAccessRoles: RouteRole[] = isAzureTargetPlatform ? [] : STANDARD_ADMINS;
const apiIntegrationsRoles: RouteRole[] = isAzureTargetPlatform ? [] : DATA_GATEWAY_STANDARD;

// Keys correspond to `RoutePath` members. Lookups are membership-based, so
// array order is not significant for guard/menu behaviour.
export const routeAccess = {
  // Top-level guarded branches (consumed by RootRouter)
  dashboard: ALL_DASHBOARD_ROLES,
  dataRoot: EXTENDED_ADMINS,
  administrationRoot: EXTENDED_ADMINS,

  // Data Access
  dataAccessRoot: dataAccessRoles,
  dataAccessMyAccessRoot: ANALYST_UP,
  dataAccessManageRoot: CLIENT_ADMIN_ONLY,

  // Data Retention
  dataRetentionRoot: EXTENDED_ADMINS,
  dataRetentionPolicyList: EXTENDED_ADMINS,
  dataRetentionPolicyCreateRoot: STANDARD_ADMINS,
  dataRetentionLogs: EXTENDED_ADMINS,

  // Notifications
  dataNotificationsRoot: STANDARD_ADMINS,
  dataNotificationsAllDefaultRoot: SUPER_ONLY,
  dataNotificationsAllDefaultProductTemplateRoot: SUPER_ONLY,
  dataNotificationsAllTenantRoot: SUPER_ONLY,
  dataNotificationsAllTenantProductTemplateRoot: SUPER_ONLY,
  dataNotificationsAllCompanyRoot: STANDARD_ADMINS,
  dataNotificationsAllCompanyProductTemplateRoot: STANDARD_ADMINS,
  dataNotificationsEmailLogsAccount: SUPER_SUPPORT,
  dataNotificationsAuditDefaultRoot: SUPER_ONLY,
  dataNotificationsAuditTenantRoot: SUPER_ONLY,
  dataNotificationsAuditCompanyRoot: STANDARD_ADMINS,

  // Data Publish Configuration
  dataPublishRoot: SUPER_SUPPORT,

  // Administration — Users / SSO / PGP
  administrationUserRoot: STANDARD_ADMINS,
  administrationSsoRoot: STANDARD_ADMINS,
  administrationPgpEncryptionRoot: STANDARD_ADMINS,

  // Administration — API Integrations
  apiIntegrationsRoot: apiIntegrationsRoles,
  apiIntegrationsThirdPartyRoot: DATA_GATEWAY_ANALYST_UP,
  apiIntegrationsUsageMonitoring: DATA_GATEWAY_SUPER_SUPPORT,

  // Administration — Products Configuration
  administrationProducts: SUPER_SUPPORT,

  // Administration — Onboarding
  administrationOnboardingRoot: EXTENDED_ADMINS,
  administrationOnboardingCompanyList: EXTENDED_ADMINS,
  administrationOnboardingCompanyViewRoot: EXTENDED_ADMINS,
  administrationOnboardingCompanyUpdateRoot: EXTENDED_ADMINS,
  administrationOnboardingCompanyCreateRoot: ANALYST_UP,
  administrationOnboardingCompanyUpdateCreateScheme: STANDARD_ADMINS,
  administrationOnboardingCompanyUpdateProducts: STANDARD_ADMINS,
  administrationOnboardingCompanyViewProducts: STANDARD_ADMINS,
  administrationOnboardingCompanyUpdateSchemeAccessRoot: ANALYST_UP,
  administrationOnboardingCompanyViewSchemeAccessRoot: ANALYST_UP,
  administrationOnboardingCompanyUpdateAudit: STANDARD_ADMINS,
  administrationOnboardingCompanyViewAudit: STANDARD_ADMINS,
  administrationOnboardingOnboardRoot: EXTENDED_ADMINS,
  administrationOnboardingBulkUploadHistory: EXTENDED_ADMINS,
  administrationOnboardingBulkUpload: BULK_UPLOAD_CONFIG_ADMINS,
  administrationOnboardingTenantRoot: SUPER_ONLY,

  // Administration — Reports
  administrationReportsRoot: STANDARD_ADMINS,
};
